'use client';

import { Suspense } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { getPartnerBranding, type PartnerBranding } from '@/config/partnerBranding';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { appendPartnerToPath, usePartnerSlug } from '@/lib/partner';
import { IS_CN_SITE } from '@/config/site';
import { cn } from '@/lib/utils';

interface LogoProps {
  className?: string;
  showText?: boolean;
  withWords?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  noLink?: boolean;
  monochrome?: boolean;
}

const LOGO_ASSET_VERSION = '20260522';

const logoAsset = (path: string) => `${path}?v=${LOGO_ASSET_VERSION}`;

const iconSizeClasses = {
  sm: 'size-6',
  md: 'size-7',
  lg: 'size-[34px]',
  xl: 'size-[44px]',
  '2xl': 'size-[56px]',
  '3xl': 'size-[72px]'
} as const;

const iconImageSizes = {
  sm: '24px',
  md: '28px',
  lg: '34px',
  xl: '44px',
  '2xl': '56px',
  '3xl': '72px'
} as const;

const wordmarkSizeClasses = {
  sm: 'h-6 w-[87px]',
  md: 'h-7 w-[101px]',
  lg: 'h-[35px] w-[126px]',
  xl: 'h-[48px] w-[173px]',
  '2xl': 'h-[56px] w-[202px]',
  '3xl': 'h-[68px] w-[245px]'
} as const;

const wordmarkImageSizes = {
  sm: '87px',
  md: '101px',
  lg: '126px',
  xl: '173px',
  '2xl': '202px',
  '3xl': '245px'
} as const;

const badgeIconClasses = {
  sm: 'h-7 w-18',
  md: 'h-9 w-24',
  lg: 'h-10 w-28',
  xl: 'h-12 w-36',
  '2xl': 'h-14 w-42',
  '3xl': 'h-18 w-50'
} as const;

const separatorSizeClasses = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
  '2xl': 'text-xl',
  '3xl': 'text-2xl'
} as const;

function PartnerBadge({ logoUrl, name, size, slug, showWordmark }: { logoUrl: string; name: string; size: NonNullable<LogoProps['size']>; slug: string; showWordmark: boolean }) {
  return (
    <img
      src={logoUrl}
      alt={name}
      className={cn('object-contain', showWordmark && 'translate-y-1', showWordmark && slug === 'chaociyuan' && '-translate-x-1', badgeIconClasses[size])}
    />
  );
}

interface LogoInnerProps extends LogoProps {
  partnerSlug: string | null;
}

function LogoInner({
  className,
  showText = true,
  withWords,
  size = 'md',
  noLink = false,
  monochrome = false,
  partnerSlug
}: LogoInnerProps) {
  const showWordmark = withWords ?? showText;
  const wrapperClassName = showWordmark ? wordmarkSizeClasses[size] : iconSizeClasses[size];
  const imageSizes = showWordmark ? wordmarkImageSizes[size] : iconImageSizes[size];
  const alt = showWordmark ? 'Musein logo with wordmark' : 'Musein logo';
  const branding = getPartnerBranding(partnerSlug);
  const { theme } = useTheme();
  const { language } = useLanguage();

  const partnerLogoUrl = branding
    ? (() => {
        const siteLogos = IS_CN_SITE ? branding.logo.cn : branding.logo.ai;
        return theme === 'dark'
          ? language === 'zh' ? siteLogos.darkZh : siteLogos.darkEn
          : language === 'zh' ? siteLogos.lightZh : siteLogos.lightEn;
      })()
    : undefined;

  const partnerName = branding
    ? language === 'zh'
      ? branding.name.zh
      : branding.name.en
    : undefined;

  const content = (
    <div className={cn('relative shrink-0', wrapperClassName)}>
      {monochrome && !showWordmark ? (
        <Image
          src={logoAsset('/logo.svg')}
          alt={alt}
          fill
          sizes={imageSizes}
          className="object-contain grayscale dark:brightness-0 dark:invert"
        />
      ) : (
        <>
          <Image
            src={logoAsset(showWordmark ? '/logo_withWords_black.svg' : '/logo.svg')}
            alt={alt}
            fill
            sizes={imageSizes}
            className="object-contain dark:hidden"
          />
          <Image
            src={logoAsset(showWordmark ? '/logo_withWords_white.svg' : '/logo.svg')}
            alt={alt}
            fill
            sizes={imageSizes}
            className="hidden object-contain dark:block"
          />
        </>
      )}
    </div>
  );

  const body = branding && partnerLogoUrl && partnerName ? (
    <div className={cn("flex items-center -translate-y-1", showWordmark ? 'items-end' : 'items-center')}>
      {content}
      {!showWordmark && <span className={cn("text-zinc-400 dark:text-zinc-500 ml-2", separatorSizeClasses[size])}>×</span>}
      <PartnerBadge logoUrl={partnerLogoUrl} name={partnerName} size={size} slug={partnerSlug ?? ''} showWordmark={showWordmark} />
    </div>
  ) : (
    content
  );

  if (noLink) {
    return <div className={cn('inline-flex items-center', className)}>{body}</div>;
  }

  return (
    <Link
      href={branding ? appendPartnerToPath('/', branding.slug) : '/'}
      className={cn('inline-flex items-center origin-left transition-transform hover:scale-105', className)}
    >
      {body}
    </Link>
  );
}

function PartnerAwareLogo(props: LogoProps) {
  const partnerSlug = usePartnerSlug();
  return <LogoInner partnerSlug={partnerSlug} {...props} />;
}

export function Logo(props: LogoProps) {
  return (
    <Suspense fallback={<LogoInner partnerSlug={null} {...props} />}>
      <PartnerAwareLogo {...props} />
    </Suspense>
  );
}
